import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Star, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * PostPurchaseRatingModal — prompted after checkout success (or from the
 * Parent Ledger "Rate this seller" affordance) so a buyer's parent can
 * leave 1-5 stars + a short note. Persists via
 * POST /api/orders/{order_id}/rating.
 */
export default function PostPurchaseRatingModal({
  open,
  onOpenChange,
  orderId,
  sellerKidId,
  sellerName,
  onSubmitted,     // (review) => void — called after a successful save
}) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!(rating >= 1 && rating <= 5)) { setErr("Pick a rating from 1 to 5 stars."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${orderId}/rating`, {
        rating,
        note: note.trim() || undefined,
        seller_kid_id: sellerKidId,
      });
      setSuccess(true);
      onSubmitted?.(data?.review);
      setTimeout(() => onOpenChange(false), 1200);
    } catch (ex) {
      setErr(formatApiError(ex) || "Could not save your feedback. Try again.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setRating(5); setHover(0); setNote(""); setErr(""); setSuccess(false); setBusy(false);
  };

  const displayValue = hover || rating;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden shadow-pop" data-testid="post-purchase-rating-modal">
        <div className="px-6 pt-6 pb-4 bg-gradient-to-br from-brandPink/10 via-white to-brandTeal/10 border-b border-slate-100">
          <div className="pill bg-amber-100 text-amber-800"><Star className="w-3 h-3 mr-1 fill-amber-500 text-amber-500" /> Rate this seller</div>
          <DialogHeader className="mt-2 space-y-1 text-left">
            <DialogTitle className="font-display text-2xl font-semibold text-brandSlate">
              How was your purchase from {sellerName || "this seller"}?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 leading-relaxed">
              Your rating helps other families shop with confidence. It also gives {sellerName || "the seller"} a little confidence boost!
            </DialogDescription>
          </DialogHeader>
        </div>
        <form onSubmit={submit} noValidate className="px-6 pt-5 pb-6 space-y-4">
          <div>
            <div className="flex items-center gap-1.5" data-testid="rating-stars" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setRating(v)}
                  onMouseEnter={() => setHover(v)}
                  className="p-1 hover:scale-110 transition-transform"
                  aria-label={`${v} star${v === 1 ? "" : "s"}`}
                  data-testid={`rating-star-${v}`}
                >
                  <Star
                    className={`w-8 h-8 transition ${v <= displayValue ? "text-amber-400 fill-amber-400" : "text-slate-300"}`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm font-bold text-slate-700 tabular-nums" data-testid="rating-value">
                {displayValue}/5
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-brandSlate mb-1">Leave a short note (optional)</label>
            <textarea
              rows={3}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Was the toy as described? Did it arrive quickly? Kind words help small sellers grow!"
              className="input-base resize-none"
              data-testid="rating-note"
              disabled={busy}
            />
            <div className="text-[11px] text-slate-400 mt-1 text-right">{note.length}/500</div>
          </div>

          {err && (
            <div className="rounded-xl bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800" data-testid="rating-error">{err}</div>
          )}
          {success && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800 flex items-center gap-1.5" data-testid="rating-success">
              <Check className="w-3.5 h-3.5" /> Thanks! Your feedback is live on the seller&rsquo;s shop.
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 btn-secondary"
              data-testid="rating-skip"
              disabled={busy}
            >
              Skip
            </button>
            <button
              type="submit"
              disabled={busy || success}
              className="flex-1 btn-primary disabled:opacity-60"
              data-testid="rating-submit"
            >
              {busy
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving&hellip;</>)
                : success
                  ? (<><Check className="w-4 h-4" /> Saved</>)
                  : (<><Star className="w-4 h-4" /> Post review</>)}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
